import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  CheckCircle,
  AlertCircle,
  Loader2,
  MessageCircle,
} from "lucide-react";


const ConsultationPaymentCallback = () => {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("Verifying payment...");

  const whatsappNumber = "2348069804062";

  const successWhatsappText = useMemo(() => {
    return encodeURIComponent(`Hello Doctor`);
  }, []);

  const failedWhatsappText = useMemo(() => {
    return encodeURIComponent(
      `Hello Zaviramed, I need a new payment link for my per-consultation payment.`
    );
  }, []);

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        const query = new URLSearchParams(window.location.search);
        const reference = query.get("reference");

        if (!reference) {
          setStatus("error");
          setMessage("Invalid payment reference.");
          return;
        }

        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/verify-payment-per-consultation`,
          { reference }
        );

        console.log(res)
        if (res.data.success) {
          setStatus("success");
          setMessage(
            "Payment successful. You can now continue your consultation on WhatsApp."
          );
        } else {
          setStatus("error");
          setMessage(res.data.message || "Payment verification failed.");
        }
      } catch (err) {
        console.error(err);
        setStatus("error");
        setMessage(
          "We could not verify this payment. Please request a new payment link on WhatsApp."
        );
      }
    };

    verifyPayment();
  }, []);

  const openSuccessWhatsapp = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=${successWhatsappText}`,
      "_blank"
    );
  };

  const openFailedWhatsapp = () => {
    window.open(
      `https://wa.me/${whatsappNumber}?text=${failedWhatsappText}`,
      "_blank"
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-[#F7F8FD] to-[#ECFFFB] flex items-center justify-center px-4 sm:px-6 py-8">
      <div className="w-full max-w-lg bg-white border border-gray-100 shadow-2xl rounded-3xl p-6 sm:p-8 md:p-10 text-center relative overflow-hidden">
        
        {/* Decorative Background Glow */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-[#25DAC5]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#1E0E62]/5 rounded-full blur-2xl"></div>

        {/* Logo */}
        <div className="flex justify-center mb-6 relative z-10">
          <img
            src={'/zavira_nobg.png'}
            alt="Zaviramed Logo"
            className="h-14 sm:h-16 object-contain"
          />
        </div>

        {/* Status Icon */}
        <div className="flex justify-center mb-5 relative z-10">
          {status === "loading" && (
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-[#25DAC5]/10 flex items-center justify-center">
              <Loader2 className="animate-spin text-[#25DAC5]" size={34} />
            </div>
          )}

          {status === "success" && (
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="text-green-600" size={36} />
            </div>
          )}

          {status === "error" && (
            <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="text-red-600" size={36} />
            </div>
          )}
        </div>

        {/* Heading */}
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#1E0E62] mb-3 relative z-10">
          Consultation Payment
        </h1>

        {/* Message */}
        <p className="text-gray-600 text-sm sm:text-base leading-6 sm:leading-7 mb-6 relative z-10 px-2">
          {message}
        </p>

        {/* Buttons */}
        <div className="relative z-10">
          {status === "success" && (
            <button
              onClick={openSuccessWhatsapp}
              className="w-full inline-flex items-center justify-center gap-3 rounded-2xl bg-green-600 hover:bg-green-700 active:scale-[0.98] text-white font-semibold px-5 py-3.5 sm:py-4 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <MessageCircle size={20} className="hidden lg:block" />
              Continue Consultation on WhatsApp
            </button>
          )}

          {status === "error" && (
            <button
              onClick={openFailedWhatsapp}
              className="w-full inline-flex items-center justify-center gap-3 rounded-2xl bg-[#25D366] hover:opacity-90 active:scale-[0.98] text-white font-semibold px-5 py-3.5 sm:py-4 transition-all duration-200 shadow-md hover:shadow-lg"
            >
              <MessageCircle size={20} className="hidden lg:block" />
              Request New Payment Link
            </button>
          )}

          {status === "loading" && (
            <div className="text-sm sm:text-base text-gray-400">
              Please wait while we confirm your payment.
            </div>
          )}
        </div>

        {/* Footer Note */}
        <p className="mt-6 text-xs sm:text-sm text-gray-400 relative z-10">
          Secure payment verification powered by Zaviramed
        </p>
      </div>
    </div>
  );
};

export default ConsultationPaymentCallback;